﻿
import { defineComponent } from 'vue';
import { response_booking } from "../../../tms/booking_engine/objects/v1_3/booking/search/response_booking";
import { Booking } from "../../booking_engine/services/booking";
import { Date_Service } from "../../system/services/date_service";
import details_dialog from "./details/details_dialog.vue";
import { Booking_Status_Class } from "./booking_status";

let Book: Booking = new Booking();
let Date_Services: Date_Service = new Date_Service();

type SortKey = 'departure' | 'id' | 'reference' | 'lead' | 'booking' | 'consultant' | 'status' | 'trip';

const Columns: { key: SortKey, label: string, optional?: boolean }[] = [
    { key: 'trip',       label: 'Trip' },
    { key: 'departure',  label: 'Departure' },
    { key: 'id',         label: 'Booking ID' },
    { key: 'reference',  label: 'Agent Ref' },
    { key: 'lead',       label: 'Lead Guest' },
    { key: 'booking',    label: 'Booked', optional: true },
    { key: 'consultant', label: 'Consultant', optional: true },
    { key: 'status',     label: 'Status' }
];

function Empty_Filters(): Record<SortKey, string> {
    return { trip: '', departure: '', id: '', reference: '', lead: '', booking: '', consultant: '', status: '' };
}

// Consultant and Status filter with a dropdown of the values in the column (exact match); '' = all
type SelectKey = 'consultant' | 'status';
const NONE_VALUE = '__none__';      // rows with no value ("—" in the table)

// Departure and Booked filter with a date picker: one day, a range (either end optional) or a calendar month.
// All values are yyyy-mm-dd (month: yyyy-mm).
type DateKey = 'departure' | 'booking';
type DateMode = 'day' | 'range' | 'month';
interface Date_Filter { mode: DateMode; day: string; from: string; to: string; month: string; }

function Empty_Date_Filter(mode: DateMode = 'day'): Date_Filter {
    return { mode, day: '', from: '', to: '', month: '' };
}

const Month_Names = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const pad2 = (n: number): string => String(n).padStart(2, '0');
const Local_Day = (d: Date): string => d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
const Shift_Day = (days: number): string => { const d = new Date(); d.setDate(d.getDate() + days); return Local_Day(d); };

// a booking's date as yyyy-mm-dd: take the date part as given (no timezone shift), else parse it
function Day_Key(raw: string): string {
    if (!raw) return '';
    const m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/.exec(raw);
    if (m) return m[1] + '-' + pad2(Number(m[2])) + '-' + pad2(Number(m[3]));
    const d = new Date(raw);
    return isNaN(d.getTime()) ? '' : Local_Day(d);
}

function Format_Day(day: string): string {
    if (!day) return '';
    const [y, m, d] = day.split('-').map(Number);
    return d + ' ' + Month_Names[m - 1].slice(0, 3) + ' ' + y;
}

function Format_Month(month: string): string {
    if (!month) return '';
    const [y, m] = month.split('-').map(Number);
    return Month_Names[m - 1] + ' ' + y;
}

export default defineComponent({
    components: {
        details_dialog
    },
    emits: ['bookings_changed'],
    props: {
        bookings: {
            type: Array as () => response_booking[] | undefined,
            required: false
        }
    },
    data() {
        return {
            sortKey: 'departure' as SortKey,
            sortAsc: true,
            searching: false,
            namesExpandedAll: false,
            loadingIndex: -1,
            columns: Columns,
            filters: Empty_Filters(),
            activeFilter: null as SortKey | null,
            detailsOpen: false,
            dateFilters: { departure: Empty_Date_Filter(), booking: Empty_Date_Filter() } as Record<DateKey, Date_Filter>,
            datePopover: null as DateKey | null,
            datePopoverPos: { top: 0, left: 0 } as { top: number, left: number }
        }
    },
    beforeUnmount() {
        this.Remove_Popover_Listeners();
    },
    methods: {
        Is_Date_Key(key: SortKey): key is DateKey {
            return key === 'departure' || key === 'booking';
        },
        Is_Select_Key(key: SortKey): key is SelectKey {
            return key === 'consultant' || key === 'status';
        },
        // the distinct values in the column, sorted; "(none)" last when some rows have no value
        Select_Options(key: SelectKey): { value: string, label: string }[] {
            const seen = new Set<string>();
            let has_none = false;
            (this.bookings ?? []).forEach(b => {
                const v = this.valueForFilter(b, key).trim();
                if (v) seen.add(v); else has_none = true;
            });
            const options = Array.from(seen)
                .sort((a, c) => a.localeCompare(c, undefined, { sensitivity: 'base' }))
                .map(v => ({ value: v, label: v }));
            if (has_none) options.push({ value: NONE_VALUE, label: '(none)' });
            return options;
        },
        Select_Matches(b: response_booking, key: SelectKey): boolean {
            const v = this.valueForFilter(b, key).trim();
            return this.filters[key] === NONE_VALUE ? v === '' : v === this.filters[key];
        },
        Raw_Date(b: response_booking, key: DateKey): string {
            return key === 'departure' ? (b?.trips?.[0]?.departure_date ?? "") : (b?.trips?.[0]?.booking_date ?? "");
        },
        Date_Filter_Active(key: DateKey): boolean {
            const f = this.dateFilters[key];
            if (f.mode === 'day') return f.day !== '';
            if (f.mode === 'month') return f.month !== '';
            return f.from !== '' || f.to !== '';
        },
        Date_Filter_Summary(key: DateKey): string {
            const f = this.dateFilters[key];
            if (f.mode === 'day') return Format_Day(f.day);
            if (f.mode === 'month') return Format_Month(f.month);
            if (f.from && f.to) return f.from === f.to ? Format_Day(f.from) : Format_Day(f.from) + ' – ' + Format_Day(f.to);
            if (f.from) return 'From ' + Format_Day(f.from);
            if (f.to) return 'Until ' + Format_Day(f.to);
            return '';
        },
        Date_Matches(b: response_booking, key: DateKey): boolean {
            const f = this.dateFilters[key];
            const day = Day_Key(this.Raw_Date(b, key));
            if (!day) return false;
            if (f.mode === 'day') return day === f.day;
            if (f.mode === 'month') return day.startsWith(f.month + '-');
            return (f.from === '' || day >= f.from) && (f.to === '' || day <= f.to);
        },
        // months that occur in the bookings for this column, newest last, for the Month picker
        Date_Months(key: DateKey): { value: string, label: string }[] {
            const seen = new Set<string>();
            (this.bookings ?? []).forEach(b => {
                const day = Day_Key(this.Raw_Date(b, key));
                if (day) seen.add(day.slice(0, 7));
            });
            return Array.from(seen).sort().map(value => ({ value, label: Format_Month(value) }));
        },
        Set_Date_Mode(key: DateKey, mode: DateMode) {
            if (this.dateFilters[key].mode === mode) return;
            // switching mode starts that mode empty, so a hidden value never keeps filtering
            this.dateFilters[key] = Empty_Date_Filter(mode);
        },
        // quick picks: relative to today, as a range or a month
        Date_Quick_Picks(key: DateKey): { label: string, apply: () => void }[] {
            const today = Local_Day(new Date());
            const this_month = today.slice(0, 7);
            const next = new Date(); next.setDate(1); next.setMonth(next.getMonth() + 1);
            const next_month = Local_Day(next).slice(0, 7);
            const set = (f: Date_Filter) => { this.dateFilters[key] = f; };

            if (key === 'departure') {
                return [
                    { label: 'Today', apply: () => set({ ...Empty_Date_Filter('day'), day: today }) },
                    { label: 'Next 7 days', apply: () => set({ ...Empty_Date_Filter('range'), from: today, to: Shift_Day(7) }) },
                    { label: 'Next 30 days', apply: () => set({ ...Empty_Date_Filter('range'), from: today, to: Shift_Day(30) }) },
                    { label: 'This month', apply: () => set({ ...Empty_Date_Filter('month'), month: this_month }) },
                    { label: 'Next month', apply: () => set({ ...Empty_Date_Filter('month'), month: next_month }) },
                ];
            }
            return [
                { label: 'Today', apply: () => set({ ...Empty_Date_Filter('day'), day: today }) },
                { label: 'Last 7 days', apply: () => set({ ...Empty_Date_Filter('range'), from: Shift_Day(-7), to: today }) },
                { label: 'Last 30 days', apply: () => set({ ...Empty_Date_Filter('range'), from: Shift_Day(-30), to: today }) },
                { label: 'This month', apply: () => set({ ...Empty_Date_Filter('month'), month: this_month }) },
            ];
        },
        Open_Date_Popover(key: DateKey, event?: Event) {
            if (this.datePopover === key) {
                this.Close_Date_Popover();
                return;
            }

            // fixed position under the column header: the table scrolls horizontally, which would clip a popover inside it
            const th = (event?.currentTarget as HTMLElement | null) ?? (this.$el as HTMLElement).querySelector('th.filter-th');
            const rect = th?.getBoundingClientRect();
            const width = 320;
            this.datePopoverPos = {
                top: (rect?.bottom ?? 0) + 6,
                left: Math.max(8, Math.min((rect?.left ?? 0), window.innerWidth - width - 8))
            };
            this.datePopover = key;
            this.activeFilter = key;

            this.Remove_Popover_Listeners();
            // next tick, so the click that opened it doesn't count as an outside click
            setTimeout(() => {
                document.addEventListener('mousedown', this.On_Popover_Outside);
                window.addEventListener('resize', this.Close_Date_Popover);
                window.addEventListener('scroll', this.On_Popover_Scroll, true);
            });
        },
        Close_Date_Popover() {
            this.datePopover = null;
            this.activeFilter = null;
            this.Remove_Popover_Listeners();
        },
        On_Popover_Outside(e: MouseEvent) {
            const pop = document.querySelector('.mb-date-pop');
            if (pop && pop.contains(e.target as Node)) return;
            this.Close_Date_Popover();
        },
        // scrolling the page moves the header away from the fixed popover; scrolling inside the popover is fine
        On_Popover_Scroll(e: Event) {
            const pop = document.querySelector('.mb-date-pop');
            if (pop && pop.contains(e.target as Node)) return;
            this.Close_Date_Popover();
        },
        Remove_Popover_Listeners() {
            document.removeEventListener('mousedown', this.On_Popover_Outside);
            window.removeEventListener('resize', this.Close_Date_Popover);
            window.removeEventListener('scroll', this.On_Popover_Scroll, true);
        },
        Clear_Date_Filter(key: DateKey) {
            this.dateFilters[key] = Empty_Date_Filter(this.dateFilters[key].mode);
        },
        // the native date picker opens on a click anywhere in the field, not just its small icon
        Open_Picker(e: Event) {
            const input = e.target as HTMLInputElement & { showPicker?: () => void };
            try { input.showPicker?.(); } catch { /* unsupported: typing still works */ }
        },
        Column_Label(key: SortKey): string {
            return Columns.find(c => c.key === key)?.label ?? '';
        },
        Lead_Passenger(b: response_booking): string {
            const p = b?.passengers?.[0];
            return (p?.first_name ?? "") + " " + (p?.family_name ?? "");
        },
        Trip_Name(b: response_booking): string {
            return b?.trips?.[0]?.trip_name ?? "";
        },
        Short_Trip_Name(b: response_booking, maxLen = 24): string {
            const full = this.Trip_Name(b);
            return full.length <= maxLen ? full : full.slice(0, maxLen - 1) + '…';
        },
        Toggle_Names_All() {
            this.namesExpandedAll = !this.namesExpandedAll;
        },
        Display_Trip_Name(b: response_booking, maxLen = 24): string {
            return this.namesExpandedAll ? this.Trip_Name(b) : this.Short_Trip_Name(b, maxLen);
        },
        Departure_Date_Display(b: response_booking): string {
            return Date_Services.display_date(b?.trips?.[0]?.departure_date ?? "");
        },
        Booking_Date_Display(b: response_booking): string {
            return Date_Services.display_date(b?.trips?.[0]?.booking_date ?? "");
        },
        Booking_ID(b: response_booking): string {
            return String((b as any)?.group_booking_id ?? (b as any)?.id ?? "");
        },
        Agent_Reference(b: response_booking): string {
            return (b as any)?.agent_reference ?? "";
        },
        Get_Consultant(b: response_booking): string {
            return b?.trips?.[0]?.affiliate_contact ?? "";
        },
        Booking_Status(b: response_booking): string {
            return b?.trips?.[0]?.passengers?.[0]?.status ?? "";
        },
        setSort(key: SortKey) {
            if (this.sortKey === key) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
        },
        Is_Filter_Open(key: SortKey): boolean {
            if (this.Is_Date_Key(key)) {
                return this.datePopover === key || this.Date_Filter_Active(key);
            }
            return this.activeFilter === key || this.filters[key] !== '';
        },
        Open_Filter(key: SortKey, event?: Event) {
            if (this.Is_Date_Key(key)) {
                this.Open_Date_Popover(key, event);
                return;
            }
            this.activeFilter = key;
            this.$nextTick(() => {
                const field = (this.$el as HTMLElement).querySelector<HTMLInputElement | HTMLSelectElement>(`[data-filter="${key}"]`);
                field?.focus();
                // dropdowns: open the list straight away where the browser allows it
                if (field instanceof HTMLSelectElement) {
                    try { (field as HTMLSelectElement & { showPicker?: () => void }).showPicker?.(); } catch { /* focus is enough */ }
                }
            });
        },
        Close_Filter(key: SortKey) {
            if (this.activeFilter === key) {
                this.activeFilter = null;
            }
        },
        Clear_Filter(key: SortKey) {
            if (this.Is_Date_Key(key)) {
                this.Clear_Date_Filter(key);
                this.Close_Date_Popover();
                return;
            }
            this.filters[key] = '';
            this.activeFilter = null;
        },
        Clear_All_Filters() {
            this.filters = Empty_Filters();
            this.dateFilters = { departure: Empty_Date_Filter(), booking: Empty_Date_Filter() };
            this.Close_Date_Popover();
        },
        Status_Class(b: response_booking): string {
            return Booking_Status_Class(this.Booking_Status(b));
        },
        valueForFilter(b: response_booking, key: SortKey): string {
            switch (key) {
            case 'trip':        return this.Trip_Name(b);
            case 'departure':   return this.Departure_Date_Display(b);
            case 'id':          return this.Booking_ID(b);
            case 'reference':   return this.Agent_Reference(b);
            case 'lead':        return this.Lead_Passenger(b);
            case 'booking':     return this.Booking_Date_Display(b);
            case 'consultant':  return this.Get_Consultant(b);
            case 'status':      return this.Booking_Status(b);
            default: return '';
            }
        },
        valueForSort(b: response_booking): string | number {
            switch (this.sortKey) {
            case 'lead':        return this.Lead_Passenger(b).toLowerCase();
            case 'id':          return this.Booking_ID(b).toLowerCase();
            case 'reference':   return this.Agent_Reference(b).toLowerCase();
            case 'consultant':  return this.Get_Consultant(b).toLowerCase();
            case 'status':      return this.Booking_Status(b).toLowerCase();
            case 'trip':        return this.Trip_Name(b).toLowerCase();
            case 'departure': {
                const d = b?.trips?.[0]?.departure_date ?? "";
                const t = d ? new Date(d).getTime() : 0;
                return isNaN(t) ? 0 : t;
            }
            case 'booking': {
                const d = b?.trips?.[0]?.booking_date ?? "";
                const t = d ? new Date(d).getTime() : 0;
                return isNaN(t) ? 0 : t;
            }
            default: return 0;
            }
        },
        async Booking_Details(b: response_booking, index: number) {
            const id = (b as any)?.group_booking_id ?? (b as any)?.id;
            if (!id)
            { 
                return;
            }
            this.loadingIndex = index;
            try {
                const details = await Book.Get_Book(id, this.$store.getters.get_Agent_Login);
                this.$store.commit("Setup_Booking", { Booking_Details: details });
                this.$store.commit("Initialise_Booking_Cancellation");
                this.detailsOpen = true;
            } finally {
                this.loadingIndex = -1;
            }
        },
        Close_Details(changed: boolean) {
            this.detailsOpen = false;
            if (changed) {
                this.$emit('bookings_changed');
            }
        },
        Row_Key(b: response_booking, index: number): string {
            return this.Booking_ID(b) || index.toString();
        }
    },
    computed: {
        Active_Filter_Count(): number {
            return Columns.filter(c => this.Is_Date_Key(c.key) ? this.Date_Filter_Active(c.key) : this.filters[c.key].trim() !== '').length;
        },
        Filtered_Bookings(): response_booking[] {
            const active = Columns
                .filter(c => !this.Is_Date_Key(c.key) && !this.Is_Select_Key(c.key))
                .map(c => ({ key: c.key, term: this.filters[c.key].trim().toLowerCase() }))
                .filter(f => f.term !== '');
            const selects = (['consultant', 'status'] as SelectKey[]).filter(k => this.filters[k] !== '');
            const dates = (['departure', 'booking'] as DateKey[]).filter(k => this.Date_Filter_Active(k));
            const list = this.bookings ?? [];
            if (active.length === 0 && selects.length === 0 && dates.length === 0) {
                return list.slice();
            }
            return list.filter(b =>
                active.every(f => this.valueForFilter(b, f.key).toLowerCase().includes(f.term)) &&
                selects.every(k => this.Select_Matches(b, k)) &&
                dates.every(k => this.Date_Matches(b, k)));
        },
        Sorted_Bookings(): response_booking[] {
            const list = this.Filtered_Bookings;
            const asc = this.sortAsc ? 1 : -1;
            list.sort((a, b) => {
                const va = this.valueForSort(a);
                const vb = this.valueForSort(b);
                if (va < vb)
                {
                    return -1 * asc;
                }
                if (va > vb){
                    return 1 * asc;
                }
                return 0;
            });
            return list;
        }
    }
});